import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import DecisionCard from './DecisionCard';

// Today's queue on a phone (DWLF-307; canvas board MobileBrief): one call at a time with progress.
// Swipe or the arrows MOVE between calls; deciding stays on the card's own buttons, so every decision
// still carries its reasons (Andy's option a, 1-Oct) — a swipe never takes or skips a trade.

const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };

const Arrow = ({ dir, onClick, disabled }) => (
  <ButtonBase onClick={onClick} disabled={disabled} aria-label={dir < 0 ? 'Previous decision' : 'Next decision'}
    sx={{ width: 44, height: 44, border: `1px solid ${tok('ink-15')}`, color: tok('text'), '&.Mui-disabled': { opacity: 0.35 } }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d={dir < 0 ? 'M15 5 L8 12 L15 19' : 'M9 5 L16 12 L9 19'} />
    </svg>
  </ButtonBase>
);

export default function DecisionStack({ queue, onDecided }) {
  const [index, setIndex] = useState(0);
  // Deciding removes a call from the queue; stay on a valid position.
  useEffect(() => { setIndex((i) => Math.min(i, Math.max(0, queue.length - 1))); }, [queue.length]);
  const go = (d) => setIndex((i) => Math.max(0, Math.min(queue.length - 1, i + d)));
  const swipe = useSwipeable({ onSwipedLeft: () => go(1), onSwipedRight: () => go(-1), preventScrollOnSwipe: false, trackMouse: false });
  const trade = queue[index];
  if (!trade) return null;
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${queue.length}, minmax(0, 1fr))`, gap: 0.5, mb: 1 }} aria-hidden="true">
        {queue.map((t, i) => <Box key={t.tradeId} sx={{ height: 3, backgroundColor: i === index ? tok('accent') : tok('ink-12') }} />)}
      </Box>
      <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-55'), mb: 1 }} aria-live="polite">
        Decision {index + 1} of {queue.length} · swipe to move between calls
      </Typography>
      <Box {...swipe}>
        <DecisionCard key={trade.tradeId} trade={trade} onDecided={onDecided} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Arrow dir={-1} onClick={() => go(-1)} disabled={index === 0} />
        <Arrow dir={1} onClick={() => go(1)} disabled={index >= queue.length - 1} />
      </Box>
    </Box>
  );
}
